import { FunctionComponent, useCallback, useEffect, useMemo, useRef, useState } from "react"
import React from "react"
import { Select, Spin, Tag } from "antd"
import { InstrumentsService } from "../api/services.gen"
import { InstrumentDto } from "../api"
import { useNavigate } from "react-router"

const InstrumentSearch: FunctionComponent = () => {
  const [searchResults, setSearchResults] = useState<InstrumentDto[]>([])
  const [searching, setSearching] = useState(false)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const pendingRequestRef = useRef<{ cancel: () => void } | null>(null)
  const navigate = useNavigate()

  const handleSearch = useCallback((value: string) => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current)
    }
    if (pendingRequestRef.current) {
      pendingRequestRef.current.cancel()
      pendingRequestRef.current = null
    }
    if (!value || value.length < 2) {
      setSearchResults([])
      setSearching(false)
      return
    }
    setSearching(true)
    debounceRef.current = setTimeout(() => {
      const request = InstrumentsService.search({ q: value, limit: '20' })
      pendingRequestRef.current = request
      request.then((data) => {
        setSearchResults(data.instruments ?? [])
        setSearching(false)
      }).catch((err) => {
        if (err?.name !== 'CancelError') {
          setSearchResults([])
          setSearching(false)
        }
      })
    }, 300)
  }, [])

  const options = useMemo(() =>
    searchResults.map((inst) => ({
      value: inst.code,
      label: (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>
            <b>{inst.code}</b>{' '}
            <span style={{ color: '#888' }}>{inst.name}</span>
          </span>
          <span>
            {inst.markets?.slice(0, 3).map((market) => (
              <Tag key={market.id} color={market.default ? 'blue' : undefined} style={{ marginRight: 2 }}>
                {market.symbol}
              </Tag>
            ))}
            {(inst.markets?.length ?? 0) > 3 && (
              <Tag>+{inst.markets!.length - 3}</Tag>
            )}
          </span>
        </div>
      ),
    })), [searchResults])

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
      if (pendingRequestRef.current) pendingRequestRef.current.cancel()
    }
  }, [])

  return (
    <Select
      showSearch
      placeholder="Search by name, symbol or ISIN..."
      filterOption={false}
      onSearch={handleSearch}
      onSelect={(code: string) => navigate(`/instruments/instrument/${code}`)}
      notFoundContent={null}
      style={{ width: '100%', minWidth: 350 }}
      popupMatchSelectWidth={false}
      dropdownStyle={{ width: 600 }}
      allowClear
      onClear={() => { setSearchResults([]); setSearching(false) }}
      options={options}
      loading={searching}
      dropdownRender={(menu) => (
        <div style={{ position: 'relative', minHeight: searching ? 48 : undefined }}>
          {menu}
          {searching && (
            <div style={{
              position: 'absolute', inset: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: 'rgba(255,255,255,0.6)',
            }}>
              <Spin size="small" />
            </div>
          )}
        </div>
      )}
    />
  )
}

export { InstrumentSearch }
